<script setup>
import { format } from 'date-fns';
import { computed, ref } from 'vue';

import { useOrthancStore } from '../store/dicom-web/orthanc-store';

const props = defineProps({
  study: Object,
  index: { type: Number },
  isOpen: Boolean,
});

const emit = defineEmits(['studyClicked', 'studyDeleted']);

const EQUIPMENT_TAGS = {
  InstitutionName: 'Institution',
  MagneticFieldStrength: 'Field Strength',
  Manufacturer: 'Manufacturer',
  ManufacturerModelName: 'Model',
  SoftwareVersions: 'Software',
};

const equipmentTagKeys = Object.keys(EQUIPMENT_TAGS);

const MODALITY_COLORS = {
  MR: { accent: '#378ADD', badgeBg: '#E6F1FB', badgeText: '#0C447C' },
  CT: { accent: '#1D9E75', badgeBg: '#E1F5EE', badgeText: '#085041' },
  US: { accent: '#7F77DD', badgeBg: '#EEEDFE', badgeText: '#3C3489' },
  XR: { accent: '#BA7517', badgeBg: '#FAEEDA', badgeText: '#633806' },
  PT: { accent: '#D85A30', badgeBg: '#FAECE7', badgeText: '#712B13' },
  NM: { accent: '#D4537E', badgeBg: '#FBEAF0', badgeText: '#72243E' },
};
const DEFAULT_COLOR = {
  accent: '#888780',
  badgeBg: '#F1EFE8',
  badgeText: '#444441',
};

const orthancStore = useOrthancStore();
const showDeleteDialog = ref(false);

const updateClickedStudy = () => emit('studyClicked', props.study);
const confirmDelete = () => {
  showDeleteDialog.value = true;
};

const handleDelete = async () => {
  showDeleteDialog.value = false;
  if (!props.study?.OrthancID) return;
  try {
    emit('studyDeleted', props.study);
    await orthancStore.deleteStudies([props.study.OrthancID]);
  }
  catch (e) {
    console.error('Failed to delete study:', e);
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'n/a';
  try {
    return format(
      new Date(
        dateString.slice(0, 4),
        Number.parseInt(dateString.slice(4, 6), 10) - 1,
        dateString.slice(6, 8),
      ),
      'MMM dd, yyyy',
    );
  }
  catch {
    return dateString;
  }
};

const formatOrthancDate = (dateString) => {
  if (!dateString) return 'n/a';
  const m = dateString.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (!m) return dateString;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[Number.parseInt(m[2], 10) - 1]} ${Number.parseInt(m[3], 10)}, ${m[1]} ${
    m[4]
  }:${m[5]}`;
};

const isNumOdd = computed(() => props.index % 2 !== 0);

const modalityColors = computed(() => {
  const mod = props.study?.Modality?.toUpperCase();
  return MODALITY_COLORS[mod] || DEFAULT_COLOR;
});

const accentColor = computed(() => modalityColors.value.accent);
const modalityStyle = computed(() => ({
  background: modalityColors.value.badgeBg,
  color: modalityColors.value.badgeText,
}));

// Initials from PatientName, fallback to '?'
const initials = computed(() => {
  const name = props.study?.PatientName?.trim() ?? '';
  if (!name) return '?';
  return name.slice(0, 2).toUpperCase();
});

// Badge color based on MagneticFieldStrength:
//   <= 1.5 T  → muted green
//   >  1.5 T  → muted blue
//   unknown   → neutral grey
const initialsBadgeStyle = computed(() => {
  const raw = props.study?.MagneticFieldStrength;
  const val = raw ? Number.parseFloat(raw) : null;
  if (val === null || Number.isNaN(val)) {
    return { background: 'rgba(136,135,128,0.6)', color: '#ffffff' };
  }
  if (val <= 1.5) {
    return { background: 'rgba(29,158,117,0.6)', color: '#ffffff' };
  }
  return { background: 'rgba(55,138,221,0.6)', color: '#ffffff' };
});

// Task status indicators (segmentation): pending | done | failed | timeout
function statusClass(s) {
  return {
    'status-pending': s === 'pending',
    'status-done': s === 'done',
    'status-failed': s === 'failed' || s === 'timeout',
  };
}
function statusIcon(s) {
  if (s === 'done') return 'fa-solid fa-circle-check';
  if (s === 'pending') return 'fa-solid fa-clock';
  return 'fa-solid fa-circle-check'; // failed | timeout (gray tick)
}
function statusLabel(active, done, s) {
  if (s === 'done') return done;
  if (s === 'failed') return `${done} failed`;
  if (s === 'timeout') return `${done} timed out`;
  return `${active}…`;
}

// Transient state between stable and pending metadata/segmentation task status
const isProcessing = computed(() => {
  if (!props.study || !props.study.IsStable) return false;
  if (props.study.metadataPending || props.study.segmentationPending) return false;

  const meta = props.study.metadataStatus;
  const seg = props.study.segmentationStatus;

  if (!meta || (meta !== 'done' && meta !== 'failed' && meta !== 'timeout')) {
    return true;
  }

  if (meta === 'done' && !seg) {
    return true;
  }

  return false;
});
</script>

<template>
  <div
    class="study px-3 py-2 rounded-lg"
    :class="{
      'dark-background': isNumOdd,
      'light-background': !isNumOdd,
      'open-study': isOpen,
      'study-loading': !study?.IsStable
        || study?.metadataPending
        || study?.segmentationPending
        || isProcessing,
    }"
    @mousedown="updateClickedStudy"
  >
    <!-- Accent bar -->
    <div class="accent-bar" :style="{ background: accentColor }" />

    <!-- Main layout: initials badge + content -->
    <div class="card-layout">
      <!-- Initials badge -->
      <div class="initials-badge" :style="initialsBadgeStyle">
        {{ initials }}
      </div>

      <!-- Content -->
      <div class="card-content">
        <!-- Row 1: name + date + delete -->
        <div class="top-row">
          <h3 class="patient-name">
            {{ study?.PatientName || 'Unknown' }}
          </h3>
          <div class="top-actions">
            <span v-if="study?.StudyDate" class="study-date-chip">
              {{ formatDate(study.StudyDate) }}
              <span v-if="study?.StudyTime" class="study-time">
                {{ study.StudyTime.slice(0, 2) }}:{{
                  study.StudyTime.slice(2, 4)
                }}
              </span>
            </span>
            <button
              class="delete-btn"
              title="Delete study"
              @mousedown.stop
              @click.stop="confirmDelete"
            >
              <i class="fa-solid fa-trash" />
            </button>
          </div>
        </div>
        <!-- In-progress indicator (left, one at a time). Empty/absent once done. -->
        <div v-if="!study?.IsStable || study?.metadataPending || study?.segmentationPending || isProcessing" class="loading-row">
          <div v-if="!study?.IsStable" class="loading-indicator">
            <div class="loading-pulse" />
            <span>Receiving…</span>
          </div>
          <div v-else-if="study?.metadataPending" class="loading-indicator">
            <div class="loading-pulse" />
            <span>Calculating metadata…</span>
          </div>
          <div v-else-if="study?.segmentationPending" class="loading-indicator">
            <div class="loading-pulse" />
            <span>Segmenting…</span>
          </div>
          <div v-else-if="isProcessing" class="loading-indicator">
            <div class="loading-pulse" />
            <span>Processing…</span>
          </div>
        </div>
        <!-- Row 2: study description -->
        <div class="study-description">
          <template v-if="study?.StudyDescription">
            {{ study.StudyDescription }}
          </template>
          <template v-else>
            <span class="missing">No study description available</span>
          </template>
          <span v-if="study?.SeriesCount" class="series-count">· {{ study.SeriesCount }} series</span>
        </div>
        <!-- Row 3: equipment tags -->
        <div class="tags-row">
          <span class="modality-badge" :style="modalityStyle">
            {{ study?.Modality || '?' }}
          </span>
          <template v-for="key in equipmentTagKeys" :key="key">
            <span v-if="study[key]" class="tag">
              <span class="tag-label">{{ EQUIPMENT_TAGS[key] }}</span>
              <span class="tag-sep">·</span>
              <span class="tag-value">{{ study[key]
              }}{{ key === 'MagneticFieldStrength' ? 'T' : '' }}</span>
            </span>
          </template>
        </div>

        <!-- Row 4: patient & study meta -->
        <div class="meta-row">
          <span v-if="study?.PatientID" class="meta-item">
            <span class="meta-label">ID</span>
            <span class="meta-value">{{ study.PatientID }}</span>
          </span>
          <span v-if="study?.PatientSex" class="meta-item">
            <span class="meta-label">Sex</span>
            <span class="meta-value">{{ study.PatientSex }}</span>
          </span>
          <span v-if="study?.PatientBirthDate" class="meta-item">
            <span class="meta-label">DOB</span>
            <span class="meta-value">{{
              formatDate(study.PatientBirthDate)
            }}</span>
          </span>
          <span v-if="study?.AccessionNumber" class="meta-item">
            <span class="meta-label">Acc</span>
            <span class="meta-value">{{ study.AccessionNumber }}</span>
          </span>
          <span v-if="study?.ReferringPhysicianName" class="meta-item">
            <span class="meta-label">Ref</span>
            <span class="meta-value">{{ study.ReferringPhysicianName }}</span>
          </span>
          <span v-if="study?.LastUpdate" class="meta-item">
            <span class="meta-label">Updated</span>
            <span class="meta-value">{{
              formatOrthancDate(study.LastUpdate)
            }}</span>
          </span>
          <span
            v-if="study?.IsStable && study?.metadataStatus && study?.metadataStatus !== 'done'"
            class="seg-status meta-seg-status"
            :class="statusClass(study?.metadataStatus)"
            :title="statusLabel('Metadata', 'Metadata', study?.metadataStatus)"
          >
            <i :class="statusIcon(study?.metadataStatus)" />
          </span>
          <span
            v-if="study?.IsStable && study?.segmentationStatus"
            class="seg-status meta-seg-status"
            :class="statusClass(study?.segmentationStatus)"
            :title="statusLabel('Segmentation', 'Segmentation', study?.segmentationStatus)"
          >
            <i :class="statusIcon(study?.segmentationStatus)" />
          </span>
        </div>
      </div>
    </div>

    <!-- Delete dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card class="delete-dialog-card">
        <v-card-title class="delete-dialog-title">
          Delete Study
        </v-card-title>
        <v-card-text class="delete-dialog-text">
          Are you sure you want to permanently delete this study
          <strong>{{ study?.PatientName || 'Unknown' }}</strong>
          ({{ study?.StudyDate ? formatDate(study.StudyDate) : 'no date' }})?
        </v-card-text>
        <v-card-actions class="delete-dialog-actions">
          <v-spacer />
          <v-btn variant="text" @click="showDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" variant="elevated" @click="handleDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.study {
  cursor: pointer;
  transition:
    border-color 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    background 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  border: 1px solid transparent;
  will-change: transform;
}

.dark-background {
  background: var(--md-bg);
}
.light-background {
  background: var(--md-bg-transparent);
}

.open-study {
  border-color: var(--md-primary-container);
  background: var(--brand-gradient-hover);
  box-shadow:
    0 4px 20px -2px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 188, 212, 0.2);
  z-index: 10;
}

.study:hover {
  background: var(--brand-gradient-hover);
}
.study:hover .delete-btn {
  opacity: 1;
}

/* Accent bar */
.accent-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 8px 0 0 8px;
  opacity: 0.7;
}

/* Two-column layout: badge | content */
.card-layout {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  min-width: 0;
}

/* Initials badge */
.initials-badge {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-top: 1px;
  opacity: 0.7;
}

/* Content column */
.card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Row 1: name + actions */
.top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.patient-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--md-text-primary);
  line-height: 1.25;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Row 2: study description */
.study-description {
  font-size: 1rem;
  font-weight: 400;
  color: var(--md-text-primary);
  opacity: 0.9;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.study-description.missing {
  font-style: italic;
  opacity: 0.6;
}

.series-count {
  color: var(--md-text-primary);
  opacity: 0.9;
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

/* Date chip */
.study-date-chip {
  font-size: 0.7rem;
  color: var(--md-text-primary);
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid var(--md-border, rgba(128, 128, 128, 0.3));
  border-radius: 99px;
  padding: 1px 9px;
  white-space: nowrap;
}

.study-time {
  opacity: 0.6;
  margin-left: 4px;
}

/* Row 3: equipment tags */
.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.modality-badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 99px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  flex-shrink: 0;
  opacity: 0.7;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  padding: 0 7px;
  border-radius: 99px;
  border: 1px solid var(--md-border, rgba(128, 128, 128, 0.25));
  background: rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
}

.tag-label {
  font-size: 0.6rem;
  color: var(--md-text-secondary);
  flex-shrink: 0;
}

.tag-sep {
  font-size: 0.58rem;
  color: var(--md-text-secondary);
  opacity: 0.35;
  flex-shrink: 0;
}

.tag-value {
  font-size: 0.65rem;
  font-weight: 500;
  color: var(--md-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Row 4: meta */
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
  align-items: baseline;
}

.meta-item {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
}

.meta-label {
  font-size: 0.58rem;
  font-weight: 600;
  color: var(--md-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  opacity: 0.6;
}

.meta-value {
  font-size: 0.68rem;
  color: var(--md-text-primary);
  font-weight: 400;
}

/* Delete button */
.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--md-text-secondary);
  padding: 3px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 0;
  flex-shrink: 0;
  font-size: 0.75rem;
}

.delete-btn:hover {
  color: var(--md-error, #ef4444);
  background: rgba(239, 68, 68, 0.1);
}

.delete-dialog-card {
  background-color: var(--md-surface-container-high) !important;
  border: 1px solid var(--md-outline-variant);
  border-radius: var(--container-radius);
}

.delete-dialog-title {
  color: var(--md-on-surface);
  border-bottom: 1px solid var(--md-outline-variant);
  padding-bottom: 12px;
}

.delete-dialog-text {
  color: var(--md-on-surface-variant);
  padding-top: 16px !important;
}

.delete-dialog-actions {
  border-top: 1px solid var(--md-outline-variant);
  padding: 12px 16px;
}

.study-loading {
  opacity: 0.55;
  position: relative;
  cursor: default;
}

.study-loading .accent-bar {
  animation: accent-pulse 1.8s ease-in-out infinite;
}

@keyframes accent-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.9;
  }
}

.loading-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

/* Small segmentation result marker in the name row (no extra line). */
.seg-status {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
}

.seg-status i {
  font-size: 0.75rem;
}

.meta-seg-status {
  margin-left: auto;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.65rem;
  color: var(--md-primary);
  letter-spacing: 0.03em;
  padding: 2px 0;
}

.loading-indicator i {
  font-size: 0.7rem;
}

.status-pending {
  /* color: var(--md-primary); */
  color: rgba(230, 179, 25, 0.95);
}

.status-done {
  color: rgba(29, 158, 117, 0.95);
}

.status-failed {
  color: rgba(136, 135, 128, 0.75);
}

.loading-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--md-primary);
  animation: pulse-dot 1.4s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
</style>
